import { Scrollable, StreamLainLogo } from '@affine/component';
import { Avatar } from '@affine/component/ui/avatar';
// StreamLain: UserPlanButton hidden
// import { UserPlanButton } from '@affine/core/components/affine/auth/user-plan-button';
// StreamLain: useCatchEventCallback unused after hiding plan button
// import { useCatchEventCallback } from '@affine/core/components/hooks/use-catch-event-hook';
import { AuthService, useCachedOwnAvatarUrl } from '@affine/core/modules/cloud';
import { GlobalDialogService } from '@affine/core/modules/dialogs';
import type { SettingTab } from '@affine/core/modules/dialogs/constant';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import {
  type HTMLAttributes,
  type ReactNode,
  Suspense,
  useCallback,
  useMemo,
} from 'react';

import { useGeneralSettingList } from '../general-setting';
import { useWorkspaceSettingList } from '../workspace-setting';
import * as style from './style.css';

export const UserInfo = () => {
  const account = useLiveData(useService(AuthService).session.account$);
  const avatarUrl = useCachedOwnAvatarUrl(account);

  // StreamLain: onClick unused after hiding plan button
  // const onClick = useCatchEventCallback(() => {
  //   onTabChange('plans', null);
  // }, [onTabChange]);

  if (!account) {
    // TODO(@eyhn): loading ui
    return;
  }
  return (
    <div data-testid="user-info-card" className={style.accountInfoCard}>
      <Avatar
        size={28}
        name={account.label}
        url={avatarUrl}
        className="avatar"
      />

      <div className="content">
        <div className="name-container">
          <div className="name" title={account.label}>
            {account.label}
          </div>
          {/* <UserPlanButton onClick={onClick} /> */}
        </div>

        <div className="email" title={account.email}>
          {account.email}
        </div>
      </div>
    </div>
  );
};

export const SignInButton = () => {
  const t = useI18n();
  const globalDialogService = useService(GlobalDialogService);

  return (
    <div
      className={style.accountButton}
      onClick={useCallback(() => {
        globalDialogService.open('sign-in', {});
      }, [globalDialogService])}
    >
      <div className="avatar not-sign">
        <StreamLainLogo size={20} />
      </div>

      <div className="content">
        <div className="name" title={t['com.streamlain.settings.sign']()}>
          {t['com.streamlain.settings.sign']()}
        </div>
        <div
          className="email"
          title={t['com.streamlain.setting.sign.message']()}
        >
          {t['com.streamlain.setting.sign.message']()}
        </div>
      </div>
    </div>
  );
};

type SettingSidebarItemProps = {
  isActive: boolean;
  icon: ReactNode;
  title: string;
  testId?: string;
  beta?: boolean;
} & HTMLAttributes<HTMLDivElement>;

const SettingSidebarItem = ({
  isActive,
  icon,
  title,
  testId,
  beta,
  ...props
}: SettingSidebarItemProps) => {
  return (
    <div
      {...props}
      title={title}
      data-testid={testId}
      className={clsx(style.sidebarSelectItem, {
        active: isActive,
      })}
    >
      <div className={style.sidebarSelectItemIcon}>{icon}</div>
      <div className={style.sidebarSelectItemName}>{title}</div>
      {beta ? <div className={style.sidebarSelectItemBeta}>Beta</div> : null}
    </div>
  );
};

const SettingSidebarGroup = ({
  title,
  items,
}: {
  title: string;
  items: (SettingSidebarItemProps & { key: string; section?: string })[];
}) => {
  // Render items with section labels inserted when the section changes
  let lastSection: string | undefined;
  const elements: ReactNode[] = [];

  for (const { key, section, ...props } of items) {
    if (section && section !== lastSection) {
      elements.push(
        <div key={`section-${section}`} className={style.sidebarSectionLabel}>
          {section}
        </div>
      );
      lastSection = section;
    }
    elements.push(<SettingSidebarItem key={key} {...props} />);
  }

  // sl-1dyb: Don't render empty groups (e.g. workspace section for guests)
  if (items.length === 0) return null;

  return (
    <div className={style.sidebarGroup}>
      <div className={style.sidebarSubtitle}>{title}</div>
      <div className={style.sidebarItemsWrapper}>{elements}</div>
    </div>
  );
};

export const SettingSidebar = ({
  activeTab,
  onTabChange,
  mobileOpen = false,
}: {
  activeTab: SettingTab;
  onTabChange: (key: SettingTab) => void;
  mobileOpen?: boolean;
}) => {
  const t = useI18n();
  const loginStatus = useLiveData(useService(AuthService).session.status$);
  const generalList = useGeneralSettingList();
  const workspaceSettingList = useWorkspaceSettingList();
  const gotoTab = useCallback(
    (tab: SettingTab) => {
      track.$.settingsPanel.menu.openSettings({ to: tab });
      onTabChange(tab);
    },
    [onTabChange]
  );

  const groups = useMemo(() => {
    const res = [
      {
        key: 'setting:general',
        title: t['com.streamlain.settingSidebar.settings.general'](),
        items: generalList.filter(item => item.key !== 'about'),
      },
      {
        key: 'setting:workspace',
        title: t['com.streamlain.settingSidebar.settings.workspace'](),
        items: workspaceSettingList,
      },
    ].map(group => {
      return {
        ...group,
        items: group.items.map(item => {
          return {
            ...item,
            isActive: item.key === activeTab,
            'data-event-arg': item.key,
            onClick: () => gotoTab(item.key),
          };
        }),
      };
    });
    return res;
  }, [activeTab, generalList, gotoTab, t, workspaceSettingList]);
  const aboutItem = useMemo(() => {
    const item = generalList.find(item => item.key === 'about');
    if (!item) return null;
    const { key, ...itemProps } = item;

    return {
      ...itemProps,
      isActive: activeTab === key,
      'data-event-arg': key,
      onClick: () => gotoTab(key),
    };
  }, [activeTab, generalList, gotoTab]);

  return (
    <div
      className={clsx(
        style.settingSlideBar,
        mobileOpen && style.settingSlideBarMobileOpen
      )}
      data-testid="settings-sidebar"
    >
      <div className={style.sidebarTitle}>
        {t['com.streamlain.settingSidebar.title']()}
      </div>

      {loginStatus === 'unauthenticated' ? <SignInButton /> : null}
      {loginStatus === 'authenticated' ? (
        <Suspense>
          <UserInfo />
        </Suspense>
      ) : null}

      <Scrollable.Root className={style.sidebarScrollArea}>
        <Scrollable.Viewport>
          <div className={style.sidebarGroupsContainer}>
            {groups.map(group => (
              <SettingSidebarGroup
                key={group.key}
                title={group.title}
                items={group.items}
              />
            ))}
          </div>
          <Scrollable.Scrollbar />
        </Scrollable.Viewport>
      </Scrollable.Root>
      {aboutItem ? (
        <div className={style.sidebarFooter}>
          <SettingSidebarItem {...aboutItem} />
        </div>
      ) : null}
    </div>
  );
};
